import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Message } from '@journal/ui';
import { PiedDePage } from '../components/PiedDePage.tsx';
import { ADRESSE_CONTACT } from '../lib/contact.ts';
import {
  champsManquants, DPO, EDITEUR, formeJuridique, HEBERGEUR, MEDIATEUR, nomVendeur,
} from '../lib/editeur.ts';

/**
 * Version des conditions générales en vigueur.
 *
 * Doit correspondre à `VERSION_CGV` côté serveur : la confirmation
 * d'abonnement envoyée au client nomme cette version, et il doit pouvoir la
 * retrouver sur la page.
 */
export const VERSION_CGV = '20 octobre 2026';

/**
 * Version du contrat de sous-traitance affichée.
 *
 * Doit correspondre à `VERSION_SOUS_TRAITANCE` côté serveur, qui refuse toute
 * autre valeur à l'inscription. Deux constantes plutôt qu'un appel : le texte
 * est ici, la version doit l'être aussi — et modifier l'un sans l'autre
 * provoque un refus visible plutôt qu'une acceptation muette du mauvais texte.
 */
export const VERSION_SOUS_TRAITANCE = 'sous-traitance-2026-09-23';

/**
 * Pages légales — mentions, conditions de vente, confidentialité.
 *
 * ── Ce que ces pages sont, et ce qu'elles ne sont pas ───────────────────────
 * Elles décrivent ce que l'application FAIT RÉELLEMENT : ce qui est collecté,
 * combien de temps, qui y accède, ce qui se passe à la résiliation. Ces
 * réponses-là viennent du code, pas d'un modèle recopié — et ce sont
 * précisément celles qu'un juriste facture cher quand il doit les reconstituer
 * lui-même.
 *
 * Ce qui reste à compléter est encadré par `À COMPLÉTER` : identité de
 * l'éditeur, SIRET, adresse, hébergeur, médiateur de la consommation. Ces
 * informations n'existent nulle part dans le code, et aucune page ne peut être
 * publiée sans elles.
 *
 * ── Elles n'engagent que si elles sont relues ───────────────────────────────
 * Des conditions de vente engagent la responsabilité de l'éditeur. Le texte
 * ci-dessous est une TRAME fidèle au produit, à faire valider avant toute
 * vente réelle. Le bandeau d'avertissement s'affiche tant que les mentions
 * obligatoires ne sont pas renseignées.
 */

/**
 * Ce qui manque encore, calculé et non déclaré.
 *
 * Un drapeau à baisser à la main reste levé : celui-ci tombe tout seul le jour
 * où le dernier champ obligatoire est renseigné dans `lib/editeur.ts`.
 */
const MANQUANTS = champsManquants();
const A_COMPLETER = MANQUANTS.length > 0;

function Page({ titre, chapeau, children }: {
  titre: string;
  chapeau: string;
  children: ReactNode;
}) {
  return (
    <div className="min-h-dvh bg-[var(--color-bg)]">
      <div className="mx-auto max-w-[46rem] px-5 py-12">
        <Link
          to="/offres"
          className="text-[14px] text-[var(--color-fg-muted)] hover:text-[var(--color-fg)]"
        >
          ← Journal de Classe
        </Link>

        <h1 className="mt-6 text-[28px] font-semibold tracking-tight">{titre}</h1>
        <p className="mt-2 text-[15px] leading-relaxed text-[var(--color-fg-muted)]">
          {chapeau}
        </p>

        {A_COMPLETER && (
          <div className="mt-6">
            <Message ton="attention" titre="Document à compléter et à faire relire">
              <p>
                {'Ce texte décrit fidèlement le fonctionnement du service, mais il '}
                {'n’a pas été relu par un juriste et il manque encore des mentions '}
                {'obligatoires : il ne doit pas servir de base à une vente réelle '}
                {'en l’état.'}
              </p>
              <p className="mt-2">
                {'À renseigner dans '}
                <code>apps/web/src/lib/editeur.ts</code>
                {' : '}
                {MANQUANTS.join(', ')}.
              </p>
            </Message>
          </div>
        )}

        <div className="mt-8 flex flex-col gap-8">{children}</div>

        <PiedDePage />
      </div>
    </div>
  );
}

function Bloc({ titre, children }: { titre: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="text-[18px] font-semibold tracking-tight">{titre}</h2>
      <div
        className={[
          'mt-3 flex flex-col gap-3 text-[14px] leading-relaxed',
          'text-[var(--color-fg-muted)] [&_strong]:text-[var(--color-fg)]',
        ].join(' ')}
      >
        {children}
      </div>
    </section>
  );
}

/**
 * L'adresse de contact publiée.
 *
 * La LCEN exige un moyen de contact DIRECT dans les mentions légales : un
 * formulaire seul n'y suffit pas, il faut une adresse qu'on puisse recopier
 * dans son propre logiciel de messagerie. Le formulaire vient en plus, pour
 * ceux qui n'en ont pas sous la main.
 */
function AdresseDeContact() {
  if (!ADRESSE_CONTACT) {
    return <AComplet>À COMPLÉTER : adresse électronique de contact</AComplet>;
  }
  return (
    <a href={`mailto:${ADRESSE_CONTACT}`} className="underline">
      {ADRESSE_CONTACT}
    </a>
  );
}

/**
 * Une mention de l'éditeur, ou la marque de ce qui manque.
 *
 * Toutes les pages passent par là : aucune ne peut afficher une valeur
 * inventée, et aucune ne peut oublier de signaler un blanc.
 */
function Mention({ valeur, quoi }: { valeur: string | null; quoi: string }) {
  if (!valeur) return <AComplet>{`À COMPLÉTER : ${quoi}`}</AComplet>;
  return <>{valeur}</>;
}

/** Marque visible d'une information que seul l'éditeur peut fournir. */
function AComplet({ children }: { children: ReactNode }) {
  return (
    <span
      className={[
        'rounded bg-[var(--color-warning-soft)] px-1.5 py-0.5',
        'font-medium text-[var(--color-warning)]',
      ].join(' ')}
    >
      {children}
    </span>
  );
}

// ── Mentions légales ────────────────────────────────────────────────────────

export function MentionsLegales() {
  return (
    <Page
      titre="Mentions légales"
      chapeau="Qui édite ce service, qui l’héberge, et comment nous joindre."
    >
      <Bloc titre="Éditeur du service">
        <p>
          <strong><Mention valeur={nomVendeur()} quoi="nom et prénom (suivis d’office de « EI »)" /></strong>
          {', '}
          <Mention valeur={formeJuridique()} quoi="forme juridique" />
          {EDITEUR.capital ? `, au capital de ${EDITEUR.capital}` : ''}.
        </p>
        <p>
          {EDITEUR.entrepreneurIndividuel ? 'Adresse : ' : 'Siège social : '}
          <Mention valeur={EDITEUR.siege} quoi="adresse complète du siège" />
          <br />
          {'SIRET : '}
          <Mention valeur={EDITEUR.siret} quoi="numéro à 14 chiffres" />
          {EDITEUR.rcs !== null && (
            <>
              <br />
              {'RCS : '}
              {EDITEUR.rcs}
            </>
          )}
          <br />
          {'TVA : '}
          {EDITEUR.franchiseDeTva
            ? 'TVA non applicable, article 293 B du code général des impôts.'
            : <Mention valeur={EDITEUR.tvaIntracommunautaire} quoi="numéro de TVA intracommunautaire" />}
        </p>
        <p>
          {'Directeur de la publication : '}
          <Mention valeur={EDITEUR.directeurPublication} quoi="nom et prénom" />
          <br />
          {'Téléphone : '}
          <Mention valeur={EDITEUR.telephone} quoi="numéro appelable par un client" />
          <br />
          {'Courriel : '}
          <AdresseDeContact />
          <br />
          {'Formulaire : '}
          <Link to="/contact" className="underline">nous écrire</Link>
        </p>
      </Bloc>

      <Bloc titre="Hébergement">
        <p>
          {'Les données sont hébergées par '}
          <strong><Mention valeur={HEBERGEUR.nom} quoi="nom de l’hébergeur" /></strong>
          {', '}
          <Mention valeur={HEBERGEUR.adresse} quoi="adresse de l’hébergeur" />
          {HEBERGEUR.telephone ? `, ${HEBERGEUR.telephone}` : ''}.
        </p>
        <p>
          <strong>{`Les serveurs sont situés dans l’${HEBERGEUR.pays}.`}</strong>
          {' Aucune donnée d’élève n’est transférée hors de l’Union européenne.'}
        </p>
      </Bloc>

      <Bloc titre="Propriété intellectuelle">
        <p>
          Le référentiel du programme scolaire reproduit les publications du
          ministère de l’Éducation nationale. Le travail saisi par les
          enseignants (préparations, progressions, appréciations) leur
          appartient : il est exportable à tout moment depuis l’application, dans
          un format ouvert et relisible.
        </p>
      </Bloc>
    </Page>
  );
}

// ── Conditions générales ────────────────────────────────────────────────────

export function ConditionsGenerales() {
  return (
    <Page
      titre="Conditions générales de vente et d’utilisation"
      chapeau={`Ce qui est vendu, à quel prix, pour combien de temps, et comment y mettre fin. Version du ${VERSION_CGV}.`}
    >
      <Bloc titre="Vendeur">
        <p>
          {'Le service est vendu par '}
          <strong><Mention valeur={nomVendeur()} quoi="nom et prénom (suivis d’office de « EI »)" /></strong>
          {', '}
          <Mention valeur={EDITEUR.siege} quoi="adresse du siège" />
          {', SIRET '}
          <Mention valeur={EDITEUR.siret} quoi="numéro à 14 chiffres" />
          {'. Téléphone : '}
          <Mention valeur={EDITEUR.telephone} quoi="numéro appelable par un client" />
          {'. Courriel : '}
          <AdresseDeContact />
          {'. Les '}
          <Link to="/mentions-legales" className="underline">mentions légales</Link>
          {' reprennent ces informations.'}
        </p>
      </Bloc>

      <Bloc titre="Objet">
        <p>
          Journal de Classe est un service en ligne destiné aux professeurs des
          écoles : cahier journal, progressions, programmation, emploi du temps
          et suivi des acquis des élèves.
        </p>
        <p>
          Deux offres existent. L’offre <strong>Enseignant</strong> ouvre un
          compte unique. L’offre <strong>École</strong> associe un{' '}
          <strong>forfait d’établissement</strong> à un montant{' '}
          <strong>par siège</strong> : le nombre de comptes est choisi à la
          souscription, avec un minimum de deux, et le total est affiché avant
          tout paiement.
        </p>
        <p>
          {/*
            * Le détail chiffré n'est PAS recopié ici.
            *
            * Les montants vivent chez le prestataire de paiement, et un
            * contrat qui les répète se contredit au premier changement de
            * tarif — la version signée disant alors autre chose que la
            * facture. Ce paragraphe décrit la STRUCTURE du prix, qui elle
            * engage, et renvoie au récapitulatif pour les montants.
            */}
          Ajouter un compte en cours d’abonnement ajuste le montant à
          l’échéance suivante, sans nouvelle souscription.
        </p>
      </Bloc>

      <Bloc titre="Période d’essai">
        <p>
          Tout espace nouvellement créé bénéficie de{' '}
          <strong>trente jours d’essai</strong>, sans carte bancaire et sans
          engagement. L’essai donne accès à l’ensemble des fonctionnalités.
        </p>
        <p>
          À son terme, sans souscription, l’espace se limite à la facturation et
          à l’<strong>export des données</strong>, qui reste toujours possible.
          Rien n’est effacé : tout revient dès la souscription.
        </p>
      </Bloc>

      <Bloc titre="Prix et paiement">
        <p>
          {EDITEUR.franchiseDeTva
            ? <>Les prix indiqués sur la page des offres sont des prix nets :{' '}
                <strong>TVA non applicable, article 293 B du code général des impôts</strong>.{' '}</>
            : <>Les prix sont indiqués <strong>toutes taxes comprises</strong> sur la
                page des offres.{' '}</>}
          Le paiement est traité par Stripe : aucune donnée de carte bancaire ne
          transite par nos serveurs ni n’y est conservée.
        </p>
        <p>
          Les factures sont émises à chaque échéance et restent accessibles
          depuis l’écran <strong>Abonnement</strong>.
        </p>
      </Bloc>

      <Bloc titre="Durée, reconduction et résiliation">
        <p>
          L’abonnement est conclu pour <strong>un mois ou un an</strong>, selon
          la périodicité choisie à la souscription, et se{' '}
          <strong>reconduit tacitement</strong> à chaque échéance, jusqu’à
          résiliation.
        </p>
        <p>
          La résiliation s’effectue <strong>en ligne, à tout moment</strong>,
          depuis l’écran Abonnement (bouton « Résilier »), accessible en
          quelques clics depuis n’importe quelle page du service, conformément à
          l’article L215-1-1 du code de la consommation. Elle prend effet au
          terme de la période en cours, déjà payée : le service reste utilisable
          jusque-là, et aucun nouveau prélèvement n’intervient.
        </p>
        <p>
          {/*
            * Loi Chatel : pour un abonnement annuel reconduit tacitement, le
            * professionnel informe le consommateur de sa faculté de ne pas
            * reconduire, au plus tôt trois mois et au plus tard un mois avant
            * le terme (article L215-1). L'écrire ici ne suffit pas : c'est un
            * courriel qui doit partir, et il figure au CHANGELOG comme tel.
            */}
          Pour un abonnement <strong>annuel</strong>, un courriel rappelle la
          faculté de ne pas reconduire, au plus tard un mois avant l’échéance
          (article L215-1 du code de la consommation). À défaut d’un tel
          rappel, la résiliation peut intervenir à tout moment après la
          reconduction, et les sommes versées au-delà sont remboursées.
        </p>
        <p>
          <strong>Après résiliation, les données restent accessibles</strong> en
          lecture et en export pendant <strong>quatre-vingt-dix jours</strong>.
          Passé ce délai, le service ayant cessé, elles ne sont plus conservées
          et sont définitivement supprimées.
        </p>
        <p>
          Ce délai n’est pas une formalité. Il couvre le temps dont vous
          disposez légalement pour demander une copie de vos données (article
          12.3 du RGPD) et, pour une école, celui qui vous permet d’exercer le
          choix entre suppression et restitution que la loi vous réserve. Il
          est surtout calé sur l’année scolaire : une résiliation en juin
          n’aboutit à un effacement qu’après la rentrée, quand quelqu’un est là
          pour s’en apercevoir.{' '}
          <strong>Quatre avertissements écrits</strong> le rappellent (à la fin
          du contrat, puis à trente, quatorze et trois jours du terme), et
          l’export reste ouvert jusqu’au dernier jour. Reprendre un abonnement
          annule la suppression, sans perte.
        </p>
        <p>
          La suppression peut aussi être demandée <strong>immédiatement</strong>,
          depuis les réglages de l’espace, sans attendre ce délai : elle
          intervient alors après trente jours, pendant lesquels la demande peut
          être annulée.
        </p>
      </Bloc>

      {/*
        * Défaut de paiement.
        *
        * Cette clause n'est pas optionnelle : la suspension du service et la
        * suppression des données doivent être ANNONCÉES pour être opposables.
        * L'exception d'inexécution (article 1219 du code civil) suppose que le
        * client sache à quoi il s'expose, et une suppression non annoncée
        * serait une clause abusive au sens de l'article L212-1 du code de la
        * consommation.
        *
        * La rédaction évite soigneusement de présenter la suppression comme
        * une SANCTION — « payez ou nous détruisons » est coercitif. Elle la
        * motive par ce qui la justifie réellement : le service ayant cessé, la
        * conservation n'a plus d'objet (article 5(1)(e) du RGPD).
        */}
      <Bloc titre="Défaut de paiement">
        <p>
          En cas de prélèvement refusé, notre prestataire de paiement effectue
          plusieurs nouvelles tentatives. Pendant cette période, le service
          reste <strong>pleinement utilisable</strong>.
        </p>
        <p>
          Si le paiement n’aboutit toujours pas, l’abonnement est suspendu :
          l’espace se limite à la facturation et à l’<strong>export des
          données</strong>, qui reste toujours possible, car le droit à la
          portabilité ne se suspend pas. La mise à jour du moyen de paiement
          rétablit tout immédiatement.
        </p>
        <p>
          Faute de régularisation dans un délai de{' '}
          <strong>quatre-vingt-dix jours à compter de la suspension</strong>, le
          contrat prend fin. Le service ayant cessé, les données n’ont plus lieu
          d’être conservées et sont alors définitivement supprimées.
        </p>
        <p>
          <strong>Quatre avertissements écrits</strong> précèdent cette
          échéance : à la suspension, puis à trente, quatorze et trois jours du
          terme. Chacun rappelle la date exacte et comporte le lien permettant
          de récupérer une copie complète des données. L’export reste ouvert
          jusqu’au dernier jour.
        </p>
      </Bloc>

      <Bloc titre="Droit de rétractation">
        <p>
          Le client consommateur dispose d’un délai de{' '}
          <strong>quatorze jours</strong> pour se rétracter, sans motif.
        </p>
        <p>
          Ce délai court à compter de la <strong>souscription de l’abonnement
          payant</strong> ; la période d’essai gratuite n’en fait pas partie.
        </p>
        <p>
          Pour que l’abonnement commence tout de suite, le client le demande
          expressément au moment du paiement (case à cocher). Il ne perd pas
          pour autant son droit de rétractation : s’il l’exerce pendant les
          quatorze jours, il ne doit que la part du prix correspondant aux{' '}
          <strong>jours déjà écoulés</strong> (article L221-25 du code de la
          consommation), et le reste lui est remboursé, sur le moyen de paiement
          utilisé, dans un délai de quatorze jours. Sans cette demande,
          l’abonnement ne commence qu’au terme du délai.
        </p>
        <p>
          La rétractation se fait <strong>en ligne</strong>, depuis l’écran
          Abonnement (bouton « Me rétracter », qui indique le montant remboursé
          avant toute confirmation), ou par écrit, de préférence par une
          demande au support de catégorie « Rétractation », accessible depuis
          l’application et depuis le courriel de confirmation. Elle met fin au
          contrat immédiatement ; un accusé de réception est adressé par
          courriel.
        </p>
        <p>
          Ce droit ne s’applique pas aux clients professionnels au sens de
          l’article liminaire du code de la consommation.
        </p>
        <p>
          Pour se rétracter par écrit, il suffit d’écrire à <AdresseDeContact /> ;
          le formulaire ci-dessous, conforme à l’annexe de l’article R221-1 du
          code de la consommation, peut être recopié tel quel :
        </p>
        {/*
          * Le formulaire type doit être FOURNI, même quand la renonciation est
          * demandée au moment du paiement : les deux se cumulent, et son
          * absence prolonge le délai de rétractation.
          */}
        <blockquote className="border-l-2 border-[var(--color-border)] pl-4 italic">
          <p>
            {'À l’attention de '}
            <Mention valeur={nomVendeur()} quoi="nom de l’éditeur" />
            {', '}
            <Mention valeur={EDITEUR.siege} quoi="adresse du siège" />
            {', '}
            {ADRESSE_CONTACT ?? 'adresse électronique'}
            {' :'}
          </p>
          <p className="mt-2">
            Je vous notifie par la présente ma rétractation du contrat portant
            sur la prestation de services ci-dessous : abonnement à Journal de
            Classe, commandé le … . Nom du consommateur : … . Adresse du
            consommateur : … . Date : … .
          </p>
        </blockquote>
      </Bloc>

      {/*
        * ── Garantie légale ──────────────────────────────────────────────────
        * Vendre un contenu numérique à un consommateur emporte une garantie de
        * conformité que le contrat ne peut pas écarter. La taire ne la fait pas
        * disparaître : elle prive seulement le client de l'information, ce qui
        * est en soi sanctionné.
        */}
      <Bloc titre="Garantie légale de conformité">
        <p>
          Le service est fourni conformément aux articles L224-25-12 et suivants
          du code de la consommation, qui garantissent au consommateur la
          <strong> conformité du contenu numérique</strong> pendant toute la
          durée de la fourniture. En cas de défaut, le client peut en exiger la
          mise en conformité sans frais et, à défaut, obtenir une réduction du
          prix ou la résolution du contrat.
        </p>
        <p>
          S’y ajoute la garantie relative aux <strong>défauts cachés</strong>
          {' '}(articles 1641 et suivants du code civil). Aucune clause des
          présentes ne réduit ces garanties.
        </p>
      </Bloc>

      <Bloc titre="Réclamations">
        <p>
          {'Toute réclamation s’adresse à '}
          <AdresseDeContact />
          {' ou par le '}
          <Link to="/contact" className="underline">formulaire de contact</Link>
          {'. Une réponse est apportée dans un délai raisonnable, et sous un '}
          {'mois pour les demandes relatives aux données personnelles.'}
        </p>
      </Bloc>

      <Bloc titre="Disponibilité et sauvegardes">
        <p>
          Le service est fourni sans garantie de disponibilité ininterrompue. Les
          données font l’objet de sauvegardes régulières, et d’un export logique
          quotidien par client permettant une reprise sélective.
        </p>
      </Bloc>

      <Bloc titre="Assistance">
        <p>
          Le prix de l’abonnement ne comprend pas d’assistance garantie : il
          n’y a <strong>pas d’engagement de délai</strong> ni de disponibilité,
          et l’éditeur ne s’engage ni à faire droit à une demande d’évolution,
          ni à accompagner l’usage du produit.
        </p>
        <p>
          Chaque message adressé au support est cependant <strong>lu</strong>,
          et les demandes sont traitées dans l’ordre de ce qui empêche de
          travailler.
        </p>
        <p>
          {/*
            * ── Ce que cette clause ne peut PAS exclure ─────────────────────
            * Vendre un contenu numérique à un consommateur emporte une garantie
            * légale de conformité (art. L224-25-12 et suivants). Une clause qui
            * l'exclurait serait réputée non écrite, et pourrait être qualifiée
            * d'abusive — ce qui fragiliserait le reste du contrat.
            *
            * La ligne de partage n'est donc pas « aide » contre « pas d'aide »,
            * mais assistance de CONFORT contre obligations LÉGALES.
            */}
          Cette clause ne s’applique pas à ce que la loi impose : la correction
          des défauts de conformité au titre de la <strong>garantie légale</strong>,
          les questions de <strong>facturation</strong>, et les demandes
          relatives aux <strong>données personnelles</strong>, ces dernières
          relevant d’un délai légal d’un mois.
        </p>
      </Bloc>

      <Bloc titre="Médiation de la consommation">
        <p>
          Conformément à l’article L612-1 du code de la consommation, le client
          consommateur peut recourir gratuitement à un médiateur de la
          consommation, après avoir tenté de régler le différend directement
          avec l’éditeur par une réclamation écrite.
        </p>
        <p>
          {'Médiateur : '}
          <strong><Mention valeur={MEDIATEUR.nom} quoi="nom du médiateur auquel l’éditeur adhère" /></strong>
          {MEDIATEUR.adresse ? `, ${MEDIATEUR.adresse}` : ''}
          {MEDIATEUR.site ? ' · ' : ''}
          {MEDIATEUR.site && <a href={MEDIATEUR.site} className="underline">{MEDIATEUR.site}</a>}
          .
        </p>
      </Bloc>

      <Bloc titre="Droit applicable">
        <p>
          Les présentes conditions sont soumises au droit français. À défaut de
          résolution amiable, le litige relève des juridictions compétentes.
        </p>
      </Bloc>
    </Page>
  );
}

// ── Politique de confidentialité ────────────────────────────────────────────

export function Confidentialite() {
  return (
    <Page
      titre="Protection des données personnelles"
      chapeau="Ce que le service collecte, pourquoi, combien de temps, et ce que vous pouvez exiger."
    >
      <Bloc titre="Contrat de sous-traitance">
        <p>
          Ce chapitre <strong>vaut contrat écrit au sens de l’article 28.3 du
          RGPD</strong>, entre l’école ou l’enseignant (responsable de
          traitement) et l’éditeur (sous-traitant). Il est accepté à la
          création de l’espace, et sa version est conservée avec la date : elle
          figure ci-dessous.
        </p>
        <p>
          <strong>Version en vigueur : {VERSION_SOUS_TRAITANCE}.</strong> Toute
          modification donne une version nouvelle, qui doit être acceptée à son
          tour.
        </p>
        <p>L’éditeur s’engage à :</p>
        <ul className="ml-5 list-disc space-y-1">
          <li>
            ne traiter les données que sur <strong>instruction documentée</strong>
            du responsable (c’est-à-dire l’usage du service), jamais pour son
            propre compte, jamais pour entraîner un modèle, jamais pour de la
            publicité&nbsp;;
          </li>
          <li>
            n’y donner accès qu’aux personnes tenues à la
            <strong> confidentialité</strong>, et pour les seuls besoins du
            support&nbsp;: chaque accès est limité à quinze minutes, motivé, et
            inscrit au journal consultable par l’école&nbsp;;
          </li>
          <li>
            mettre en œuvre les <strong>mesures de sécurité</strong> décrites
            plus bas : cloisonnement entre écoles par la base de données,
            chiffrement en transit et des sauvegardes, double authentification,
            journal d’audit&nbsp;;
          </li>
          <li>
            n’engager de <strong>sous-traitant ultérieur</strong> qu’en
            informant préalablement le responsable, qui peut s’y opposer&nbsp;;
            la liste à jour figure plus bas&nbsp;;
          </li>
          <li>
            <strong>assister</strong> le responsable pour les demandes des
            personnes (accès, effacement, portabilité), l’export et la
            suppression étant exécutables depuis l’application sans passer par
            le support&nbsp;;
          </li>
          <li>
            <strong>notifier sans délai</strong> toute violation de données, et
            fournir les éléments permettant au responsable de notifier la CNIL
            dans les 72 heures&nbsp;;
          </li>
          <li>
            au terme du service, <strong>supprimer</strong> les données après le
            délai annoncé, l’export restant possible jusque-là&nbsp;;
          </li>
          <li>
            mettre à disposition les <strong>informations nécessaires</strong>
            pour démontrer le respect de ces obligations, et se prêter aux
            audits du responsable.
          </li>
        </ul>
      </Bloc>

      <Bloc titre="Qui est responsable de quoi">
        <p>
          <strong>L’enseignant ou l’école est responsable de traitement</strong> :
          c’est lui qui décide de tenir un cahier journal et d’y consigner le
          suivi de ses élèves.
        </p>
        <p>
          <strong>L’éditeur est sous-traitant</strong> au sens de l’article 28 du
          RGPD : il traite ces données sur instruction du client, jamais pour son
          propre compte. Une demande d’un parent d’élève s’adresse donc à
          l’école, qui dispose dans l’application des moyens d’y répondre.
        </p>
      </Bloc>

      {/*
        * ── Ce dont l'éditeur est RESPONSABLE ────────────────────────────────
        * La page ne parlait que de sous-traitance, ce qui est juste pour les
        * données d'élèves — et faux pour le compte de l'enseignant, sa
        * facturation et ses échanges avec le support. Sur ceux-là, l'éditeur
        * décide seul des finalités : il en est responsable, et doit dire les
        * bases légales et les durées.
        */}
      <Bloc titre="Ce dont l’éditeur est responsable">
        <p>
          Pour <strong>le compte de l’enseignant, la facturation, le support et
          la sécurité</strong>, l’éditeur agit comme responsable de traitement :
        </p>
        <ul className="ml-5 list-disc space-y-1">
          <li>
            <strong>compte et accès</strong> (adresse électronique, nom,
            empreinte du mot de passe, sessions) : exécution du contrat
            (article 6.1.b du RGPD), conservés tant que le compte existe&nbsp;;
          </li>
          <li>
            <strong>facturation</strong> (coordonnées et factures) : obligation
            légale (article 6.1.c), conservées dix ans (article L123-22 du code
            de commerce)&nbsp;;
          </li>
          <li>
            <strong>support</strong> (messages échangés) : exécution du contrat,
            conservés le temps du suivi de la demande&nbsp;;
          </li>
          <li>
            <strong>journaux techniques et d’audit</strong> (horodatage,
            adresse IP, action) : intérêt légitime à assurer la sécurité
            (article 6.1.f), conservés un an au plus.
          </li>
        </ul>
        <p>
          {'Contact pour ces traitements : '}
          {DPO.contact
            ? <a href={`mailto:${DPO.contact}`} className="underline">{DPO.contact}</a>
            : <AdresseDeContact />}
          {DPO.nom ? ` (délégué à la protection des données : ${DPO.nom})` : ''}
          {'. Aucun délégué à la protection des données n’est requis : '}
          {'l’activité ne comporte ni suivi systématique à grande échelle, ni '}
          {'traitement de données sensibles.'}
        </p>
      </Bloc>

      <Bloc titre="Données traitées">
        <p>
          <strong>Comptes des enseignants</strong> : adresse électronique,
          empreinte du mot de passe, journal des connexions.
        </p>
        <p>
          <strong>Élèves : minimisation stricte</strong> : nom, prénom, année
          scolaire et classe. <strong>Rien d’autre.</strong> Pas de date de
          naissance, pas d’adresse, pas de coordonnées de parents, jamais de
          donnée de santé.
        </p>
        <p>
          <strong>Travail pédagogique</strong> : préparations, progressions,
          appréciations et niveaux d’acquisition renseignés par l’enseignant.
        </p>
        <p>
          <strong>Paiement</strong> : traité par Stripe. Aucune donnée bancaire
          n’est stockée par le service.
        </p>
      </Bloc>

      <Bloc titre="Ce qui n’est jamais fait">
        <p>
          Aucune donnée n’est vendue, louée, ni transmise à des fins publicitaires
          ou statistiques. Aucun profilage. Aucun traceur publicitaire. Les noms
          et prénoms d’élèves ainsi que les appréciations sont{' '}
          <strong>masqués dans les journaux techniques</strong>.
        </p>
      </Bloc>

      <Bloc titre="Isolation entre écoles">
        <p>
          Les données de chaque école sont isolées par le moteur de base de
          données lui-même, et non par le seul code applicatif : une requête sans
          filtre ne renvoie aucune ligne, plutôt que celles d’un autre client.
          Cette isolation est vérifiée automatiquement à chaque livraison.
        </p>
      </Bloc>

      <Bloc titre="Durées de conservation">
        <p>
          <strong>Données pédagogiques et élèves</strong> : conservées tant que
          l’espace existe. Une durée de conservation peut être fixée par l’école,
          au terme de laquelle les élèves les plus anciens sont supprimés.
        </p>
        <p>
          <strong>Sessions et jetons</strong> : sept jours après expiration.{' '}
          <strong>Journal d’audit</strong> : un an. <strong>Exports
          quotidiens</strong> : sept jours. <strong>Traces techniques
          d’exécution</strong> : quatre-vingt-dix jours.
        </p>
        <p>
          <strong>Demandes d’assistance</strong> : un fil de support est
          conservé <strong>deux ans après sa clôture</strong>, messages
          compris. Un fil encore ouvert n’est jamais supprimé : il vous est
          dû. Les messages envoyés depuis le{' '}
          <Link to="/contact" className="underline">formulaire de contact
          public</Link> sont conservés <strong>un an après leur
          traitement</strong> ; non traités, ils restent.
        </p>
        <p>
          <strong>Invitations</strong> : l’adresse invitée et son jeton sont
          effacés <strong>trente jours</strong> après acceptation, révocation
          ou expiration. Passé ce délai, le membre figure dans la liste des
          membres et l’invitation n’apprend plus rien.
        </p>
        <p>
          <strong>Suppression d’un espace</strong> : l’accès est coupé
          immédiatement : toutes les sessions sont fermées et l’espace devient
          inaccessible. L’effacement définitif intervient{' '}
          <strong>trente jours plus tard</strong> : ce délai existe pour pouvoir
          revenir sur une suppression demandée par erreur, et il peut être
          interrompu à tout moment par le propriétaire de l’espace.
        </p>
        <p>
          <strong>Fin du contrat</strong>, qu’elle vienne d’une résiliation ou
          d’un abonnement impayé : la fin du service fait courir un délai de{' '}
          <strong>quatre-vingt-dix jours</strong> au terme duquel les données
          sont supprimées, faute de raison de les conserver plus longtemps.
          Quatre avertissements écrits précèdent cette échéance, et l’export
          reste possible jusqu’au dernier jour. Toute reprise de l’abonnement
          l’annule.
        </p>
        <p>
          Deux catégories survivent à cet effacement, et c’est délibéré. Les{' '}
          <strong>factures</strong>, conservées dix ans au titre de l’obligation
          comptable (article L123-22 du code de commerce) ; elles ne portent ni
          nom d’élève ni contenu pédagogique. Et le{' '}
          <strong>journal d’audit</strong>, qui garde un an la trace de qui a
          exporté ou supprimé quoi, sans jamais contenir de nom d’élève.
          Tout le reste est effacé, et un contrôle automatique refuse de
          conclure la purge s’il subsiste la moindre ligne.
        </p>
      </Bloc>

      <Bloc titre="Transferts hors de l’Union européenne">
        <p>
          <strong>Aucune donnée d’élève ni de travail pédagogique ne quitte
          l’Union européenne.</strong> L’hébergement et les sauvegardes y
          restent.
        </p>
        <p>
          Le paiement est traité par <strong>Stripe Payments Europe, Ltd.</strong>
          {' '}(Irlande). Stripe peut, pour la lutte contre la fraude et le
          support, faire intervenir des entités situées hors de l’Union : ces
          transferts sont encadrés par les clauses contractuelles types de la
          Commission européenne. Les données concernées sont celles du
          paiement, jamais celles des élèves.
        </p>
      </Bloc>

      <Bloc titre="Cookies et traceurs">
        <p>
          Le service dépose un <strong>unique cookie</strong>, strictement
          nécessaire au maintien de la session, inaccessible au JavaScript de la
          page et supprimé à la déconnexion. Aucun traceur publicitaire, aucune
          mesure d’audience tierce, aucun partage avec un réseau social.
        </p>
        <p>
          Un tel cookie est <strong>exempté de consentement</strong> (article 82
          de la loi Informatique et Libertés) : c’est pourquoi aucune bannière
          ne s’affiche, non pas par oubli, mais parce qu’il n’y a rien à
          consentir.
        </p>
      </Bloc>

      <Bloc titre="Vos droits">
        <p>
          Accès, rectification, effacement, portabilité, limitation et opposition
          s’exercent auprès de l’école responsable de traitement.
        </p>
        <p>
          L’application fournit les moyens techniques correspondants :{' '}
          <strong>export complet</strong> de l’espace dans un format ouvert, et{' '}
          <strong>suppression définitive</strong>, tous deux accessibles au
          propriétaire de l’espace.
        </p>
        <p>
          Réclamation possible auprès de la CNIL, 3 place de Fontenoy, 75007
          Paris (cnil.fr).
        </p>
      </Bloc>

      <Bloc titre="Sous-traitants ultérieurs">
        <p>
          <strong><Mention valeur={HEBERGEUR.nom} quoi="nom de l’hébergeur" /></strong>
          {` (hébergement, ${HEBERGEUR.pays}), `}
          <strong>Stripe Payments Europe, Ltd.</strong>
          {' (paiement, Irlande) et '}
          <strong>Brevo</strong>
          {' (envoi des courriels, France). Toute évolution de cette liste est portée à la '}
          {'connaissance des clients, qui peuvent s’y opposer.'}
        </p>
      </Bloc>

      <Bloc titre="Contact">
        <p>
          Pour toute demande relative à vos données : <AdresseDeContact />.
        </p>
        <p>
          {'Sans accès à votre espace, le '}
          <Link to="/contact" className="underline">formulaire de contact</Link>
          {' aboutit au même endroit.'}
        </p>
      </Bloc>
    </Page>
  );
}
